import React, { useImperativeHandle, useRef } from 'react'

import { DefaultFeatureFlags } from '@sketch-hq/sketch-web-renderer'
import { ErrorMessage } from '@sketch/components'
import { IS_EMBEDDED } from '@sketch/constants'
import { WebRendererCanvas } from '../../../../../components/WebRendererCanvas'
import { useCanvasPresentationFile } from '../../../../../components/WebRendererCanvas/hooks'
import { useTheme } from 'styled-components'

import { FrameAnnotationOverlay } from '../FrameAnnotationOverlay'
import LoadingState from '../../../components/LoadingState'
import { PageCanvasError } from '../../../PageCanvasView/components/PageCanvasError'

import { useCanvasAnnotations } from '../../../../annotations/hooks'
import { useInspectorContext, useInspectorHoveredElement } from '../../hooks'

import * as S from './FrameCanvas.styles'
import {
  FrameDetailInspectorContextMenu,
  useInspectorContextMenu,
} from '../FrameDetailInspectorContextMenu'
import { FrameDetailInspectorTestOverlay } from '../FrameDetailInspectorTestOverlay'
import { getWebRenderCursor } from '@sketch/inspector'
import { useObjectProxyAccumulator } from '@sketch/utils'
import { OriginContent } from '@sketch/canvas'
import { FrameDetailInspectorOverlay } from '../FrameDetailInspectorOverlay'
import { useCopyLinkShortcut } from '../../../../canvas/hooks'
import CanvasContextMenu, {
  useCanvasContextMenu,
} from '../../../PageCanvasView/components/CanvasContextMenu'
import { useInitialCameraPositionFromQueryParams } from '../../../PageCanvasView/hooks'

interface FrameCanvasProps {
  pageUUID: string
  // TODO: Rename artboardUUID to frameGroupUUID
  // https://linear.app/sketch/issue/WEB-510
  artboardUUID: string
  isViewingLatestVersion?: boolean
}

const ARTBOARD_DETAIL_CANVAS_FEATURE_FLAGS: Partial<
  typeof DefaultFeatureFlags
> = {
  canvasLayersEvents: true,
}

const IS_RUNNING_TESTS =
  process.env.JEST_WORKER_ID !== undefined || window.Cypress

export const FrameCanvas = React.forwardRef<HTMLDivElement, FrameCanvasProps>(
  function FrameCanvas(
    { pageUUID, artboardUUID, isViewingLatestVersion },
    ref
  ) {
    const canvasContainerRef = useRef<HTMLDivElement>(
      null
    ) as React.RefObject<HTMLDivElement>
    useImperativeHandle(ref, () => canvasContainerRef.current!)

    // Enable Cmd+shift+C shortcut to copy page/frame URL
    useCopyLinkShortcut()

    const theme = useTheme()
    const {
      isInspectorActive,
      clickHandler,
      mouseMoveHandler,
      mouseDownHandle,
    } = useInspectorContext()

    const {
      share,
      page,
      versionShortId,
      hasError,
      isLoading,
      isRendering,
      showArtboardDecorations,
    } = useCanvasPresentationFile({
      pageUUID,
    })

    const {
      isPlacingDraftAnnotation,
      onMouseDown,
      onMouseUp,
      onTouchEnd,
      onTouchStart,
      onPointerDown,
      onPointerUp,
      activeAnnotation,
      isAnnotationMoving,
    } = useCanvasAnnotations()

    const {
      contextMenuPos,
      contextMenuMetaData,
      canvasContainerRef: canvasContextMenuRef,
      handleContextMenu,
      handleCloseContextMenu: handleCloseCanvasContextMenu,
    } = useCanvasContextMenu()

    useImperativeHandle(canvasContextMenuRef, () => canvasContainerRef.current!)

    const {
      handleContextMenuEvent,
      contextMenuPosition,
      handleCloseContextMenu,
    } = useInspectorContextMenu(canvasContainerRef)
    const hoveredElement = useInspectorHoveredElement()

    const webRenderContainerProps = useObjectProxyAccumulator(
      !isInspectorActive && {
        onContextMenu: handleContextMenu,
        onMouseDown,
        onMouseUp,
        onTouchEnd,
        onTouchStart,
        onPointerDown,
        onPointerUp,
      },
      isInspectorActive && {
        onContextMenu: handleContextMenuEvent,
        onClick: clickHandler,
        onMouseMove: mouseMoveHandler,
        onMouseDown: mouseDownHandle,
      }
    )

    // Camera Position
    const initialCameraPosition = useInitialCameraPositionFromQueryParams()

    if (isLoading) {
      return <LoadingState />
    }

    if (isRendering) {
      return (
        <ErrorMessage
          title="Rendering Canvas…"
          description="The canvas is rendering. When it’s ready, it will appear here."
          icon={<S.DocumentProcessing />}
        />
      )
    }

    if (hasError || !page) {
      return (
        <PageCanvasError
          description={
            <>
              There was a problem fetching the data. You can find more
              information on our{' '}
              <a href="https://status.sketch.com/">Status Page</a>.
            </>
          }
          showReloadCanvasAction
        />
      )
    }

    return (
      <S.Wrapper ref={canvasContainerRef}>
        <WebRendererCanvas
          pageUUID={pageUUID}
          artboardUUID={artboardUUID}
          page={page}
          initialPan={initialCameraPosition?.initialPan}
          initialZoom={initialCameraPosition?.initialZoom}
          versionShortId={versionShortId}
          shareIdentifier={share?.identifier || ''}
          backgroundColor={theme.colors.background.canvas.A}
          featureFlags={ARTBOARD_DETAIL_CANVAS_FEATURE_FLAGS}
          webRenderCursor={getWebRenderCursor(
            isPlacingDraftAnnotation,
            hoveredElement,
            isAnnotationMoving && !isViewingLatestVersion
          )}
          webRenderContainerProps={webRenderContainerProps.current}
          showArtboardDecorations={showArtboardDecorations}
        >
          {!IS_EMBEDDED && (
            <OriginContent>
              <FrameAnnotationOverlay
                permanentArtboardId={artboardUUID}
                parentWrapper={canvasContainerRef}
                activeAnnotationIdentifier={activeAnnotation}
              />
            </OriginContent>
          )}

          {isInspectorActive ? (
            <>
              {IS_RUNNING_TESTS && <FrameDetailInspectorTestOverlay />}
              <FrameDetailInspectorOverlay
                canvasContainerRef={canvasContainerRef}
              />
              <FrameDetailInspectorContextMenu
                contextMenuPosition={contextMenuPosition}
                onCloseMenu={handleCloseContextMenu}
              />
            </>
          ) : (
            <CanvasContextMenu
              permanentPageId={pageUUID}
              closeTooltip={handleCloseCanvasContextMenu}
              contextMenuPos={contextMenuPos}
              contextMenuMetaData={contextMenuMetaData}
            />
          )}
        </WebRendererCanvas>
      </S.Wrapper>
    )
  }
)
